import type {
  LevelDefinition,
  LevelValidationIssue,
  RuleConfig,
} from "./types";

export const DEFAULT_RULES: RuleConfig = {
  trayCapacity: 36,
  maxTrayCapacity: 60,
  matchSize: 36,
  failOnTimeout: true,
};

export function resolveRules(
  level: LevelDefinition,
  overrides: Partial<RuleConfig> = {},
): RuleConfig {
  return { ...DEFAULT_RULES, ...level.rules, ...overrides };
}

export function validateLevel(
  level: LevelDefinition,
  overrides: Partial<RuleConfig> = {},
): readonly LevelValidationIssue[] {
  const issues: LevelValidationIssue[] = [];
  const rules = resolveRules(level, overrides);
  const beadIds = new Set<string>();
  const positions = new Set<string>();
  const targetIds = new Set<string>();

  if (!Number.isInteger(rules.trayCapacity) || rules.trayCapacity <= 0) {
    issues.push({ code: "tray-capacity", message: "trayCapacity must be a positive integer" });
  }
  if (!Number.isInteger(rules.maxTrayCapacity) || rules.maxTrayCapacity <= 0) {
    issues.push({ code: "max-tray-capacity", message: "maxTrayCapacity must be a positive integer" });
  } else if (rules.maxTrayCapacity < rules.trayCapacity) {
    issues.push({ code: "max-tray-capacity", message: "maxTrayCapacity cannot be smaller than trayCapacity" });
  }
  if (!Number.isInteger(rules.matchSize) || rules.matchSize <= 0) {
    issues.push({ code: "match-size", message: "matchSize must be a positive integer" });
  }
  if (rules.matchSize > rules.maxTrayCapacity) {
    issues.push({ code: "unreachable-match", message: "matchSize cannot exceed maxTrayCapacity" });
  }
  if (!Number.isFinite(level.durationMs) || level.durationMs <= 0) {
    issues.push({ code: "duration", message: "durationMs must be positive" });
  }

  for (const chunk of level.board.chunks) {
    for (const cell of chunk.cells) {
      const x = chunk.origin.x + cell.x;
      const y = chunk.origin.y + cell.y;
      const position = `${x},${y}`;
      if (positions.has(position)) {
        issues.push({ code: "duplicate-cell", message: `duplicate board cell at ${position}` });
      }
      positions.add(position);
      for (const bead of cell.layers) {
        if (beadIds.has(bead.id)) {
          issues.push({ code: "duplicate-bead", message: `duplicate bead id ${bead.id}` });
        }
        beadIds.add(bead.id);
      }
    }
  }

  for (const slot of level.targetSlots) {
    if (targetIds.has(slot.id)) {
      issues.push({ code: "duplicate-target", message: `duplicate target id ${slot.id}` });
    }
    targetIds.add(slot.id);
  }

  return issues;
}

export function assertValidLevel(
  level: LevelDefinition,
  overrides: Partial<RuleConfig> = {},
): void {
  const issues = validateLevel(level, overrides);
  if (issues.length > 0) {
    throw new Error(`Invalid level: ${issues.map((issue) => issue.message).join("; ")}`);
  }
}
